// R90 — **THE FIRST READ THIS SITE MAKES, AND A FAILED ONE DREW THE WHOLE HOUSE AS A VISITOR.**
//
// `loadSession` is the very first thing `App` does. Everything about who she is hangs off its one
// answer: `isLoggedIn` gates the account menu, the house inbox, the mobile drawer, the floating
// journal, Ask Lala, the Verse Search, the Craft drawer and the generation follower; `isOwner` gates
// her dashboard door; and the tier read that R87 just fixed does not even fire (`if (!isLoggedIn) {
// setServerTier(null); return; }`). It was written as:
//
//     const response = await fetch("/api/auth/session");
//     const data = await response.json();
//     if (data.member) setMember(data.member);
//     } catch { /* Session restore is optional during local development. */ }
//
// **NO STATUS CHECK, NO `else`, AND A CATCH WHOSE COMMENT IS ABOUT A DEVELOPER'S LAPTOP.** So a
// dropped request on a phone, a proxy page that is not JSON, or the seconds an instance spends
// restarting mid-deploy all land in the same place as a genuine signed-out visitor: **a member who
// IS signed in is shown the house of somebody who is not, and nothing is red anywhere.**
//
// **THE SHARPEST CASE IS THE ONE WHERE SHE JUST DID THE WORK.** The Google return path
// (`?auth=google-success`) sets `justSignedIn` and then relies on this read and on nothing else —
// the cookie is already set and verified. If this read fails there, she has just completed an OAuth
// round trip and the house tells her she is a stranger. Tapping Continue with Google again sends her
// round it a second time and lands in exactly the same place, because the failure was never auth.
// That is X8-HOTFIX's report — "the button does nothing" — arriving from a different cause.
//
// **R18's DISTINCTION IS THE WHOLE LAW HERE, AND IT IS THE ONE THIS HOUSE ALREADY WROTE DOWN.**
// `src/sealStatus.js`'s header: a status the server DELIBERATELY SENT is an ANSWER and must be acted
// on; an absent or unparseable payload is TRANSPORT NOISE and re-asking is correct. This route
// always answers `200 { member: <member|null>, consent }` — so `member: null` is a real answer and
// she is genuinely signed out, while a throw, a non-200 or a body that will not parse is not an
// answer at all. Collapsing those two is the entire defect.
//
// **THREE ANSWERS, NOT FOUR, AND THE MISSING ONE IS ARGUED RATHER THAN OMITTED** (R58/R70): there is
// no `sign-in` kind because this route IS the sign-in question, and no `refused` kind because
// `handleAuthSession` composes no refusal — it catches its own consent lookup and still answers 200.
// A branch for an answer a route cannot give is a second answer key.
export type SessionAnswer<M> =
  | { kind: "member"; member: M; consent: SessionConsent }
  | { kind: "none"; consent: SessionConsent }
  | { kind: "unreachable" };

export type SessionConsent = {
  version?: string;
  recorded?: string;
  acceptedAt?: string | null;
  reconsentRequired?: boolean;
};

// The session's own shape, moved here rather than left in App.tsx, because this transport is what
// PRODUCES it and a type belongs with the law that owns it (R11/R12). App.tsx imports it back; the
// anti-drift rule forbids it redefining one it imports.
export type MemberSession = {
  name: string;
  email: string;
  subscribed: boolean;
  plan: string;
  // Derived server-side from the verified email on every session read. Cosmetic only: it
  // decides what the UI bothers rendering, never what the server allows. Every owner-only
  // endpoint checks the role itself.
  owner?: boolean;
};

// **THE HOUSE'S LINE, IN ENGLISH BY DESIGN AND NOT A NEW DECLARATION.** Every sentence on the
// sign-in shell is English — `authErrorMessage` in `src/lib/labels.ts` owns eight of them and none is
// in `src/strings.js` — and 14-B6's own lesson is that a lone translated line over an untranslated
// screen IS the defect. It goes where `authErrorMessage`'s sentences go: the membership modal's one
// notice slot, which is exactly where the wrong conclusion becomes actionable.
export const SESSION_UNREACHABLE_NOTICE =
  "We could not check whether you are already signed in. That was this house, not your account — check your connection and try again in a moment.";

type SessionPayload<M> = { member?: M | null; consent?: SessionConsent };

const NO_CONSENT: SessionConsent = {};

// ONE round trip, and the answer it gives is about THIS attempt only. `restoreSession` below owns
// how many attempts there are; keeping the two apart is what lets a gate drive a single bad answer
// without a clock.
export async function readSession<M>(doFetch: typeof fetch = fetch): Promise<SessionAnswer<M>> {
  try {
    const res = await doFetch("/api/auth/session", { cache: "no-store" });
    // **A NON-200 IS NOT AN ANSWER THIS ROUTE GIVES.** `handleAuthSession` sends 200 on every path
    // it reaches, including the one where the consent lookup threw. So a 500 is index.js's
    // last-resort handler (which carries a raw error message and no shape at all — R89) and a 502 is
    // the proxy in front of an instance that is restarting. Neither is "she is signed out".
    if (!res.ok) { await res.body?.cancel?.().catch(() => {}); return { kind: "unreachable" }; }
    const data = (await res.json().catch(() => null)) as SessionPayload<M> | null;
    // A body that will not parse is transport noise wearing a 200 — a proxy's HTML error page is
    // the common one. It is not a member and it is not a signed-out visitor.
    if (!data || typeof data !== "object") return { kind: "unreachable" };
    const consent = (data.consent && typeof data.consent === "object" ? data.consent : NO_CONSENT) as SessionConsent;
    // **`member: null` IS THE ANSWER, AND IT IS THE WHOLE POINT OF THE DISTINCTION.** A payload that
    // arrived and says there is nobody signed in is a fact; acting on it is correct and re-asking
    // would be a poll of a question already answered.
    if (data.member) return { kind: "member", member: data.member as M, consent };
    return { kind: "none", consent };
  } catch {
    return { kind: "unreachable" };
  }
}

// **THE RE-ASK IS R18's OWN RULING APPLIED, NOT A NEW POLICY** — and it is free, because this route
// is read-only by design (`handleAuthSession`'s own comment: "restoring a session must never write a
// user row to answer a question"). It re-asks ONLY on `unreachable`; an answer of either kind stops
// it dead, so a signed-out visitor costs exactly one request as she always did.
//
// **IT DELAYS NO PAINT.** The house draws at `member: null` from the first frame regardless — the
// correction has always arrived late — so the retry is purely additive and the only thing it can
// change is how often the wrong conclusion sticks.
//
// **THE BUDGET IS DELIBERATELY SMALL AND IT IS NOT A DEPLOY WINDOW.** Two short waits, ~2.1s in all,
// which covers a dropped packet, a sleeping radio waking up and a proxy blip. ITEM 13b's readiness
// deadline is 45 seconds; retrying for that long would leave a member staring at a signed-out house
// for most of a minute with the house pretending to be busy. A restart that outlasts this budget
// gets the sentence, which is the honest answer.
//
// `doFetch` and `sleep` are PARAMETERS (R46), so a gate drives attempt three of three without a
// browser and without a clock.
export const SESSION_RETRY_DELAYS = [600, 1500];

export async function restoreSession<M>(
  doFetch: typeof fetch = fetch,
  sleep: (ms: number) => Promise<void> = (ms) => new Promise((r) => setTimeout(r, ms)),
  delays: number[] = SESSION_RETRY_DELAYS,
): Promise<SessionAnswer<M>> {
  // **THE BOUND IS THE DELAY ROSTER ITSELF, WHICH IS WHY THERE IS NO SECOND NUMBER TO KEEP IN STEP**
  // (R31): one wait per declared delay, so the attempt count is `delays.length + 1` by construction
  // and cannot drift from the budget stated above. A loop bounded only by `unreachable` never
  // reaches the sentence at all — it is a house pretending to be busy for as long as the outage
  // lasts, which is this round's own failure wearing a spinner.
  let answer = await readSession<M>(doFetch);
  for (const wait of delays) {
    if (answer.kind !== "unreachable") return answer;
    await sleep(wait);
    answer = await readSession<M>(doFetch);
  }
  return answer;
}

// **A RESTORED SESSION SAYS NOTHING AND A SIGNED-OUT ONE SAYS NOTHING** — the house appearing as
// hers IS the answer in the first case, and in the second she is exactly who the front page is
// written for (R51..R65 on every read in this house). The sentence exists for the one state that had
// no way to be told apart from the second, and it is shown where it becomes actionable rather than
// as a banner over a house nobody has asked a question of yet.
export function sessionRestoreNotice(
  answer: SessionAnswer<unknown>,
  failed: string = SESSION_UNREACHABLE_NOTICE,
): string {
  return answer.kind === "unreachable" ? failed || SESSION_UNREACHABLE_NOTICE : "";
}
